import { useState, useEffect, useRef } from 'react';
import Modal from '../../modal/modal';
import Card from '../../card/Card';
import P from '../../texts/p/P';
import DropDown from '../../dropdown/DropDown';
import Badge from '../../badge/Badge';
import Input from '../../input/Input';
import { FilterData, FilterModalProps, CommitteeRoleFilterItem } from './FilterModal.types';
import { getFilters } from '../../../services/ApplicationService';
import { toast } from 'react-toastify';
import { getStatusText } from '../../../utils/applicationStatusUtils';
import { useFiscalIdConfig } from '../../../hooks/useFiscalIdConfig';

function roleOptionLabel(r: CommitteeRoleFilterItem): string {
  return `${r.label} (${r.roleCode})`;
}

const FilterModal: React.FC<FilterModalProps> = ({
  isOpen,
  onClose,
  onApplyFilters,
  showCommercialFilter = true,
  showManagementFilter = false,
  showReferrerFilter = true,
  showProducerFilter = true,
  showStatusFilter = true,
  showCuitFilter = false,
  showPendingCommitteeRoleFilter = false,
  pendingCommitteeRoleItems = [],
  clientReferred = false,
  allowedStatusIds,
  initialFilters = {},
}) => {
  const fiscalId = useFiscalIdConfig();
  const [isLoading, setIsLoading] = useState(false);
  const [filterData, setFilterData] = useState<FilterData | null>(null);

  // states for the selected filters
  const [selectedCommercial, setSelectedCommercial] = useState<string>('');
  const [selectedReferrer, setSelectedReferrer] = useState<string>('');
  const [selectedProducer, setSelectedProducer] = useState<string>('');
  const [selectedStatus, setSelectedStatus] = useState<string>('');
  const [selectedManagement, setSelectedManagement] = useState<string>('');
  const [selectedPendingCommitteeRole, setSelectedPendingCommitteeRole] =
    useState<string>('');
  const [cuitFilter, setCuitFilter] = useState<string>('');

  const prevIsOpenRef = useRef<boolean>(false);

  useEffect(() => {
    if (isOpen) {
      loadFilters();
    }
  }, [isOpen]);

  useEffect(() => {
    const modalJustOpened = isOpen && !prevIsOpenRef.current;
    const hasInitialFilters = Object.keys(initialFilters).length > 0;

    if (modalJustOpened && filterData && hasInitialFilters) {
      if (initialFilters.commercialId && filterData.commercials) {
        const commercial = filterData.commercials.find(
          (c) => c.id.toString() === initialFilters.commercialId
        );
        if (commercial) {
          setSelectedCommercial(commercial.name);
        }
      }

      if (initialFilters.referrerName && filterData.referrers) {
        const referrer = filterData.referrers.find(
          (r) =>
            r.id.toString() === initialFilters.referrerName ||
            r.id === initialFilters.referrerName
        );
        if (referrer) {
          setSelectedReferrer(referrer.name);
        }
      }

      if (initialFilters.producerId && filterData.producers) {
        const producer = filterData.producers.find(
          (p) => p.id.toString() === initialFilters.producerId
        );
        if (producer) {
          setSelectedProducer(producer.name);
        }
      }

      if (initialFilters.status && filterData.status) {
        const status = filterData.status.find(
          (s) => s.id.toString() === initialFilters.status
        );
        if (status) {
          setSelectedStatus(getStatusText(status.id, status.name));
        }
      }

      if (initialFilters.cuit) {
        setCuitFilter(initialFilters.cuit);
      }

      if (initialFilters.managementId && filterData.managements?.length) {
        const idKey =
          initialFilters.managementId === 'null'
            ? '-1'
            : initialFilters.managementId;
        const mgmt = filterData.managements.find((m) => m.id === idKey);
        if (mgmt) {
          setSelectedManagement(mgmt.name);
        }
      }

      if (
        initialFilters.pendingCommitteeRoleCode &&
        pendingCommitteeRoleItems?.length
      ) {
        const role = pendingCommitteeRoleItems.find(
          (r) => r.roleCode === initialFilters.pendingCommitteeRoleCode
        );
        if (role) {
          setSelectedPendingCommitteeRole(roleOptionLabel(role));
        }
      }
    }

    prevIsOpenRef.current = isOpen;
  }, [isOpen, filterData, pendingCommitteeRoleItems, initialFilters?.pendingCommitteeRoleCode]);

  const loadFilters = async () => {
    setIsLoading(true);
    try {
      const referrerType = clientReferred ? 'client' : 'application';
      const response = await getFilters(referrerType);
      if (response.success && 'data' in response) {
        setFilterData(response.data);
      } else {
        toast.error('Error al cargar los filtros');
      }
    } catch (error) {
      toast.error('Error al cargar los filtros');
      console.error('Error loading filters:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleApplyFilters = () => {
    const queryParams: Record<string, string> = {};

    if (selectedCommercial) {
      const commercial = filterData?.commercials.find(
        (c: { id: number; name: string }) => c.name === selectedCommercial
      );
      if (commercial) {
        queryParams.commercialId = commercial.id.toString();
      }
    }

    if (selectedManagement) {
      const mgmt = filterData?.managements?.find((m) => m.name === selectedManagement);
      if (mgmt) {
        queryParams.managementId = mgmt.id === '-1' ? 'null' : mgmt.id;
      }
    }

    if (selectedReferrer) {
      const referrer = filterData?.referrers.find(
        (r: { id: string; name: string }) => r.name === selectedReferrer
      );
      if (referrer) {
        queryParams.referrerName = referrer.id; // Enviar el código, no el nombre
      }
    }

    if (selectedProducer) {
      const producer = filterData?.producers.find(
        (p: { id: number; name: string }) => p.name === selectedProducer
      );
      if (producer) {
        queryParams.producerId = producer.id.toString();
      }
    }

    if (selectedStatus) {
      const status = filterData?.status.find(
        (s: { id: number; name: string }) =>
          s.name === selectedStatus ||
          getStatusText(s.id, s.name) === selectedStatus
      );
      if (status) {
        queryParams.status = status.id.toString();
      }
    }

    if (cuitFilter && cuitFilter.trim() !== '') {
      queryParams.cuit = cuitFilter.trim();
    }

    if (selectedPendingCommitteeRole && pendingCommitteeRoleItems.length) {
      const role = pendingCommitteeRoleItems.find(
        (r) => roleOptionLabel(r) === selectedPendingCommitteeRole
      );
      if (role) {
        queryParams.pendingCommitteeRoleCode = role.roleCode;
      }
    }

    onApplyFilters(queryParams);
    handleClose();
  };

  const handleClose = () => {
    setSelectedCommercial('');
    setSelectedManagement('');
    setSelectedReferrer('');
    setSelectedProducer('');
    setSelectedStatus('');
    setSelectedPendingCommitteeRole('');
    setCuitFilter('');
    onClose();
  };

  const getCommercialItems = () => {
    if (!filterData?.commercials) return [];
    return filterData.commercials.map(
      (commercial: { id: number; name: string }) => ({
        label: commercial.name,
        disabled: false,
      })
    );
  };

  const getManagementItems = () => {
    if (!filterData?.managements?.length) return [];
    return filterData.managements.map((m) => ({
      label: m.name,
      disabled: false,
    }));
  };

  const getReferrerItems = () => {
    if (!filterData?.referrers) return [];
    return filterData.referrers.map(
      (referrer: { id: string; name: string }) => ({
        label: referrer.name,
        disabled: false,
        code: referrer.id,
      })
    );
  };

  const getProducerItems = () => {
    if (!filterData?.producers) return [];
    return filterData.producers.map(
      (producer: { id: number; name: string }) => ({
        label: producer.name,
        disabled: false,
      })
    );
  };

  const getPendingCommitteeRoleItems = () =>
    pendingCommitteeRoleItems.map((r) => ({
      label: roleOptionLabel(r),
      disabled: false,
    }));

  const getStatusItems = () => {
    if (!filterData?.status) return [];

    let statusList = filterData.status;

    if (allowedStatusIds && allowedStatusIds.length > 0) {
      statusList = statusList.filter((status: { id: number; name: string }) => {
        return (
          allowedStatusIds.includes(status.id.toString()) ||
          allowedStatusIds.includes(status.name)
        );
      });
    }

    return statusList.map((status: { id: number; name: string }) => ({
      label: getStatusText(status.id, status.name),
      disabled: false,
    }));
  };

  const getStatusBadgeVariant = (
    statusId: string
  ): 'success' | 'primary' | 'warning' | 'danger' | 'info' => {
    switch (statusId) {
      case 'APP':
        return 'success';
      case 'REJ':
        return 'danger';
      case 'PEN':
        return 'warning';
      case 'COM':
        return 'success';
      case 'INS':
        return 'success';
      case 'FIN':
        return 'info';
      case 'RTI':
        return 'primary';
      case 'REV':
        return 'info';
      default:
        return 'info';
    }
  };

  const renderStatusItem = (item: { label: string; disabled?: boolean }) => {
    const status = filterData?.status.find(
      (s: { id: number; name: string }) =>
        s.name === item.label ||
        getStatusText(s.id, s.name) === item.label
    );
    const statusId = status?.id?.toString() || '';

    return (
      <Badge variant={getStatusBadgeVariant(statusId)} type='text'>
        {item.label}
      </Badge>
    );
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title='Filtrar carpetas'
      width='600px'
      primaryText='Aplicar filtros'
      primaryVariant='primary'
      onPrimaryAction={handleApplyFilters}
      primaryActionDisabled={isLoading}
      secondaryText='Cancelar'
      secondaryVariant='contained'
      onSecondaryAction={handleClose}
      showOverlay={true}
      overflow='visible'
    >
      <Card
        margin={{ top: '4' }}
        borderRadius={{
          topLeft: true,
          topRight: true,
          bottomLeft: false,
          bottomRight: false,
        }}
        borderRadiusAmount='4'
      >
        <P type='3' variant='neutral' withMargin={true}>
          Seleccioná los filtros que quieras aplicar para ajustar los resultados
          según tus preferencias
        </P>

        <Card display='grid' gap='16px' margin={{ top: '4' }}>
          {showCommercialFilter && (
            <DropDown
              id='commercial-filter-dropdown'
              title={selectedCommercial || 'Comercial'}
              items={getCommercialItems()}
              onChange={setSelectedCommercial}
              searchable={true}
              searchPlaceholder='Buscar comercial...'
              disabled={isLoading || !filterData}
            />
          )}

          {showManagementFilter && (
            <DropDown
              id='management-filter-dropdown'
              title={selectedManagement || 'Gerencia'}
              items={getManagementItems()}
              onChange={setSelectedManagement}
              searchable={true}
              searchPlaceholder='Buscar gerencia...'
              disabled={isLoading || !filterData}
            />
          )}

          {showReferrerFilter && (
            <DropDown
              id='referrer-filter-dropdown'
              title={selectedReferrer || 'Referido/Vendedor'}
              items={getReferrerItems()}
              onChange={setSelectedReferrer}
              searchable={true}
              searchPlaceholder='Buscar referido o vendedor...'
              disabled={isLoading || !filterData}
            />
          )}

          {showProducerFilter && (
            <DropDown
              id='producer-filter-dropdown'
              title={selectedProducer || 'Usuario'}
              items={getProducerItems()}
              onChange={setSelectedProducer}
              searchable={true}
              searchPlaceholder='Buscar usuario...'
              disabled={isLoading || !filterData}
            />
          )}

          {showPendingCommitteeRoleFilter && pendingCommitteeRoleItems.length > 0 && (
            <DropDown
              id='pending-committee-role-filter'
              title={
                selectedPendingCommitteeRole || 'Pendiente de aprobación (rol)'
              }
              items={getPendingCommitteeRoleItems()}
              onChange={setSelectedPendingCommitteeRole}
              searchable={true}
              searchPlaceholder='Buscar rol...'
              disabled={isLoading}
            />
          )}

          {showStatusFilter && (
            <DropDown
              id='status-filter-dropdown'
              title={selectedStatus || 'Estado'}
              items={getStatusItems()}
              onChange={setSelectedStatus}
              searchable={true}
              searchPlaceholder='Buscar estado...'
              disabled={isLoading || !filterData}
              renderItem={renderStatusItem}
            />
          )}

          {showCuitFilter && (
            <Input
              type='text'
              size='md'
              placeholder={fiscalId.filterPlaceholder}
              value={cuitFilter}
              onChange={(e) => setCuitFilter(e.target.value)}
            />
          )}
        </Card>
      </Card>
    </Modal>
  );
};

export default FilterModal;
